import React, { useCallback, useEffect, useState } from 'react';
import { ActiveSession } from '@/types';
import { Shield, Users, Building2, Monitor, Globe, Clock, Laptop } from 'lucide-react';
import { api } from '@/services/api';
import { showAlertModal, showConfirmModal } from '@shared/components/common/AlertModal';
import { normalizeErrorMessage } from '@/lib/errorUtils';

type SystemStatusTabProps = {
  currentSessionId?: string | null;
  onCurrentSessionRevoked?: () => Promise<void> | void;
};

export const SystemStatusTab: React.FC<SystemStatusTabProps> = ({
  currentSessionId,
  onCurrentSessionRevoked
}) => {
  const [sessions, setSessions] = useState<ActiveSession[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [revokingSessionId, setRevokingSessionId] = useState<string | null>(null);

  const loadSessions = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await api.listStaffSessions({ activeWithinMinutes: 60 });
      setSessions(data || []);
    } catch (_err) {
      setSessions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  const activeSessions = sessions.filter((s) => s.status !== 'revoked');
  const activeStaffSessions = activeSessions.filter((s) => s.type === 'staff');
  const activeVisitorSessions = activeSessions.filter((s) => s.type === 'visitor');
  const activeCompanySessions = activeSessions.filter((s) => s.type === 'company');

  const handleRevokeSession = async (session: ActiveSession) => {
    if (!session?.id || session.status === 'revoked') return;
    const shouldRevoke = await showConfirmModal(
      `Revoke session for ${session.email || session.name}?`,
      {
        title: 'Revoke Session',
        isDestructive: true,
        confirmText: 'Revoke',
        cancelText: 'Cancel'
      }
    );
    if (!shouldRevoke) return;

    setRevokingSessionId(session.id);
    try {
      await api.revokeStaffSession(session.id);
      if (session.id === currentSessionId) {
        await onCurrentSessionRevoked?.();
        return;
      }
      await loadSessions();
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to revoke session.'));
    } finally {
      setRevokingSessionId(null);
    }
  };

  type SessionTableProps = {
    title: string;
    icon: React.ComponentType<{ className?: string }>;
    sessions: ActiveSession[];
    colorClass: { bg: string; text: string };
  };
  const SessionTable: React.FC<SessionTableProps> = ({
    title,
    icon: Icon,
    sessions,
    colorClass
  }) => (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full">
      <div
        className={`p-4 border-b border-slate-100 flex justify-between items-center ${colorClass.bg}`}
      >
        <h3 className={`font-bold flex items-center gap-2 ${colorClass.text}`}>
          <Icon className="w-5 h-5" /> {title}
        </h3>
        <span
          className={`text-xs font-bold px-2 py-1 rounded-full bg-white/60 ${colorClass.text} flex items-center gap-1`}
        >
          {sessions.length > 0 && (
            <div className="w-2 h-2 rounded-full bg-current animate-pulse"></div>
          )}
          {sessions.length} Active
        </span>
      </div>
      <div className="flex-1 overflow-auto max-h-[400px] scrollbar-thin scrollbar-thumb-slate-200">
        {isLoading ? (
          <div className="p-8 text-center text-slate-400 text-sm italic">
            Loading active sessions...
          </div>
        ) : sessions.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm italic">
            No active sessions detected in the last hour.
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-slate-500 font-semibold text-[10px] uppercase tracking-wider sticky top-0 shadow-sm">
              <tr>
                <th className="p-3">User Details</th>
                <th className="p-3">Network Info</th>
                <th className="p-3 text-right">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {sessions.map((s: ActiveSession) => (
                <tr key={s.id} className="hover:bg-slate-50 transition-colors">
                  <td className="p-3">
                    <div className="font-medium text-slate-900 text-xs sm:text-sm">{s.name}</div>
                    <div
                      className="text-[10px] text-slate-500 truncate max-w-[120px]"
                      title={s.email}
                    >
                      {s.email}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1.5 text-xs text-slate-600 font-mono bg-slate-100 px-1.5 py-0.5 rounded w-fit mb-1">
                      <Globe className="w-3 h-3 text-slate-400" /> {s.lastIp || 'Unknown'}
                    </div>
                    <div
                      className="flex items-center gap-1 text-[10px] text-slate-400 truncate max-w-[150px]"
                      title={s.userAgent || 'Unknown Device'}
                    >
                      <Laptop className="w-3 h-3 flex-shrink-0" /> {s.userAgent || 'Unknown Device'}
                    </div>
                  </td>
                  <td className="p-3 text-right text-xs text-slate-500">
                    <div className="flex items-center justify-end gap-1 mb-1">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          s.status === 'revoked'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-emerald-100 text-emerald-700'
                        }`}
                      >
                        {s.status === 'revoked' ? 'Revoked' : 'Active'}
                      </span>
                    </div>
                    <div className="flex items-center justify-end gap-1">
                      <Clock className="w-3 h-3 text-slate-400" />{' '}
                      {s.lastSeenAt || s.lastLoginAt
                        ? new Date(s.lastSeenAt || s.lastLoginAt || '').toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : '—'}
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    <button
                      onClick={() => handleRevokeSession(s)}
                      disabled={s.status === 'revoked' || revokingSessionId === s.id}
                      className="text-[10px] px-2.5 py-1.5 rounded-lg border border-red-200 text-red-600 font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-red-50"
                    >
                      {revokingSessionId === s.id ? 'Revoking...' : 'Revoke'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <Monitor className="w-6 h-6 text-teal-600" /> Live System Monitor
        </h3>
        <div className="text-xs text-slate-500 bg-slate-100 px-3 py-1 rounded-full border border-slate-200 flex items-center gap-2">
          Server Time: {new Date().toLocaleTimeString()}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SessionTable
          title="Staff Users"
          icon={Shield}
          sessions={activeStaffSessions}
          colorClass={{ bg: 'bg-purple-50', text: 'text-purple-700' }}
        />
        <SessionTable
          title="Visitors (Customers)"
          icon={Users}
          sessions={activeVisitorSessions}
          colorClass={{ bg: 'bg-teal-50', text: 'text-teal-700' }}
        />
        <SessionTable
          title="Company Accounts"
          icon={Building2}
          sessions={activeCompanySessions}
          colorClass={{ bg: 'bg-blue-50', text: 'text-blue-700' }}
        />
      </div>
    </div>
  );
};
